/**
 * Deterministic scalar helpers. The sim only uses + - * / sqrt floor (IEEE-754 exact),
 * so it reproduces bit-for-bit across browsers, devices and a future native port.
 * Transcendentals (sin/cos/atan/exp/pow) are reserved for rendering.
 */

export const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const saturate = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Cubic Hermite 3t² - 2t³ */
export const smooth3 = (t: number): number => t * t * (3 - 2 * t);
/** Derivative of smooth3. */
export const smooth3d = (t: number): number => 6 * t * (1 - t);
/** Quintic 6t⁵ - 15t⁴ + 10t³ */
export const smooth5 = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
export const smooth5d = (t: number): number => 30 * t * t * (t * (t - 2) + 1);

export const smoothstep = (e0: number, e1: number, x: number): number => smooth3(saturate((x - e0) / (e1 - e0)));

/** Remap x from [a,b] to [c,d] (unclamped). */
export const remap = (x: number, a: number, b: number, c: number, d: number): number => c + ((x - a) / (b - a)) * (d - c);

/** Deterministic exponential-ish approach factor for a fixed dt: 1 - 1/(1 + k·dt + (k·dt)²/2). */
export function approachFactor(k: number, dt: number): number {
  const x = k * dt;
  return 1 - 1 / (1 + x + x * x * 0.5);
}

/** Squared length helpers. */
export const len2 = (x: number, y: number): number => Math.sqrt(x * x + y * y);
export const len3 = (x: number, y: number, z: number): number => Math.sqrt(x * x + y * y + z * z);

/** cos of an angle in degrees via a deterministic polynomial (used for config thresholds, run once). */
export function cosDeg(deg: number): number {
  // Taylor series on radians reduced to [-π, π]; accurate to ~1e-9 for |x| ≤ π.
  let x = (deg * 3.141592653589793) / 180;
  while (x > 3.141592653589793) x -= 6.283185307179586;
  while (x < -3.141592653589793) x += 6.283185307179586;
  const x2 = x * x;
  let term = 1;
  let sum = 1;
  for (let i = 1; i < 14; i++) {
    term *= -x2 / ((2 * i - 1) * (2 * i));
    sum += term;
  }
  return sum;
}
